import type { StreamObject } from '../components/ai-chat-messages/type';
import { mergeStreamContent } from './stream-objects';

// Transport guards only. Model-specific context budgeting happens centrally
// in the backend together with every other canonical context source.
const MAX_TRANSCRIPT_MESSAGES = 256;
const MAX_TRANSCRIPT_LENGTH = 256_000;
const MAX_LABEL_LENGTH = 72;

type AIChatSelectionProps = Record<string, unknown> & {
  messages?: unknown;
  parentBlockId?: unknown;
};

type VisibleChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

function readableText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

type HistoryToken = {
  kind: 'string' | 'number' | 'literal' | 'punctuation' | 'end';
  value?: unknown;
  start: number;
  end: number;
};
type HistoryFrame = {
  kind: 'array' | 'object';
  phase: 'first' | 'next' | 'colon' | 'value' | 'after';
  value: unknown[] | Record<string, unknown> | undefined;
  key?: string;
};

/** Read the existing stored JSON with bounded lexical work, without a full parse/copy. */
class StoredHistoryReader {
  private offset: number;
  private readonly end: number;
  constructor(
    private readonly source: string,
    start = 0,
    end = source.length
  ) {
    this.offset = start;
    this.end = end;
  }

  private *token(build: boolean): Generator<void, HistoryToken> {
    yield;
    const source = this.source;
    while (this.offset < this.end && /[\t\n\r ]/.test(source[this.offset])) {
      if ((this.offset & 2047) === 0) yield;
      this.offset++;
    }
    const start = this.offset;
    if (start === this.end) return { kind: 'end', start, end: start };
    const char = source[this.offset++];
    if ('[]{}:,'.includes(char))
      return { kind: 'punctuation', value: char, start, end: this.offset };
    if (char === '"') {
      const parts: string[] = [];
      let mark = this.offset;
      while (this.offset < this.end) {
        if ((this.offset & 2047) === 0) {
          if (build) parts.push(source.slice(mark, this.offset));
          mark = this.offset;
          yield;
        }
        const current = source[this.offset++];
        if (current === '"') {
          if (build) parts.push(source.slice(mark, this.offset - 1));
          return {
            kind: 'string',
            value: build ? parts.join('') : undefined,
            start,
            end: this.offset,
          };
        }
        if (current.charCodeAt(0) < 32)
          throw new SyntaxError('Invalid history string');
        if (current !== '\\') continue;
        if (build) parts.push(source.slice(mark, this.offset - 1));
        const escaped = source[this.offset++];
        if (escaped === 'u') {
          const hex = source.slice(this.offset, this.offset + 4);
          if (hex.length !== 4 || !/^[0-9a-fA-F]{4}$/.test(hex))
            throw new SyntaxError('Invalid history escape');
          if (build) parts.push(String.fromCharCode(Number.parseInt(hex, 16)));
          this.offset += 4;
        } else {
          const escapes: Record<string, string> = {
            '"': '"',
            '\\': '\\',
            '/': '/',
            b: '\b',
            f: '\f',
            n: '\n',
            r: '\r',
            t: '\t',
          };
          if (!Object.hasOwn(escapes, escaped))
            throw new SyntaxError('Invalid history escape');
          if (build) parts.push(escapes[escaped]);
        }
        mark = this.offset;
      }
      throw new SyntaxError('Unterminated history string');
    }
    if (char === '-' || /[0-9]/.test(char)) {
      this.offset = start;
      if (source[this.offset] === '-') this.offset++;
      if (source[this.offset] === '0') this.offset++;
      else {
        if (!/[1-9]/.test(source[this.offset] ?? ''))
          throw new SyntaxError('Invalid history number');
        while (this.offset < this.end && /[0-9]/.test(source[this.offset])) {
          if ((this.offset & 2047) === 0) yield;
          this.offset++;
        }
      }
      if (source[this.offset] === '.') {
        this.offset++;
        if (!/[0-9]/.test(source[this.offset] ?? ''))
          throw new SyntaxError('Invalid history fraction');
        while (this.offset < this.end && /[0-9]/.test(source[this.offset])) {
          if ((this.offset & 2047) === 0) yield;
          this.offset++;
        }
      }
      if (source[this.offset] === 'e' || source[this.offset] === 'E') {
        this.offset++;
        if (source[this.offset] === '+' || source[this.offset] === '-')
          this.offset++;
        if (!/[0-9]/.test(source[this.offset] ?? ''))
          throw new SyntaxError('Invalid history exponent');
        while (this.offset < this.end && /[0-9]/.test(source[this.offset])) {
          if ((this.offset & 2047) === 0) yield;
          this.offset++;
        }
      }
      return {
        kind: 'number',
        value: build ? Number(source.slice(start, this.offset)) : undefined,
        start,
        end: this.offset,
      };
    }
    const literal =
      char === 't'
        ? 'true'
        : char === 'f'
          ? 'false'
          : char === 'n'
            ? 'null'
            : '';
    if (!literal || source.slice(start, start + literal.length) !== literal)
      throw new SyntaxError('Invalid history value');
    this.offset = start + literal.length;
    return {
      kind: 'literal',
      value: build
        ? literal === 'null'
          ? null
          : literal === 'true'
        : undefined,
      start,
      end: this.offset,
    };
  }

  *read(
    build: boolean
  ): Generator<
    void,
    { value: unknown; array: boolean; records: Array<[number, number]> }
  > {
    const stack: HistoryFrame[] = [];
    const records: Array<[number, number]> = [];
    let rootSeen = false;
    let array = false;
    let value: unknown;
    let recordStart: number | undefined;
    const accept = (item: unknown) => {
      const frame = stack.at(-1);
      if (!frame) {
        value = item;
        rootSeen = true;
        return;
      }
      if (build && frame.kind === 'array')
        (frame.value as unknown[]).push(item);
      if (build && frame.kind === 'object') {
        Object.defineProperty(frame.value!, frame.key!, {
          value: item,
          enumerable: true,
          configurable: true,
          writable: true,
        });
      }
      frame.phase = 'after';
    };
    while (true) {
      const token = yield* this.token(build);
      const frame = stack.at(-1);
      if (!frame && rootSeen) {
        if (token.kind !== 'end')
          throw new SyntaxError('Trailing history data');
        return { value, array, records };
      }
      if (token.kind === 'end') throw new SyntaxError('Incomplete history');
      if (frame?.phase === 'after') {
        if (token.kind === 'punctuation' && token.value === ',') {
          frame.phase = 'next';
          continue;
        }
        const close = frame.kind === 'array' ? ']' : '}';
        if (token.kind !== 'punctuation' || token.value !== close)
          throw new SyntaxError('Invalid history separator');
        stack.pop();
        if (
          !build &&
          array &&
          stack.length === 1 &&
          recordStart !== undefined
        ) {
          records.push([recordStart, token.end]);
          recordStart = undefined;
        }
        continue;
      }
      if (
        frame?.phase === 'first' &&
        token.kind === 'punctuation' &&
        token.value === (frame.kind === 'array' ? ']' : '}')
      ) {
        stack.pop();
        if (
          !build &&
          array &&
          stack.length === 1 &&
          recordStart !== undefined
        ) {
          records.push([recordStart, token.end]);
          recordStart = undefined;
        }
        continue;
      }
      if (
        frame?.kind === 'object' &&
        (frame.phase === 'first' || frame.phase === 'next')
      ) {
        if (token.kind !== 'string')
          throw new SyntaxError('Invalid history key');
        frame.key = token.value as string;
        frame.phase = 'colon';
        continue;
      }
      if (frame?.phase === 'colon') {
        if (token.kind !== 'punctuation' || token.value !== ':')
          throw new SyntaxError('Invalid history property');
        frame.phase = 'value';
        continue;
      }
      if (!build && array && stack.length === 1) recordStart = token.start;
      if (token.kind === 'punctuation') {
        if (token.value !== '[' && token.value !== '{')
          throw new SyntaxError('Invalid history value');
        const kind = token.value === '[' ? 'array' : 'object';
        if (!rootSeen && !frame) array = kind === 'array';
        const item = build ? (kind === 'array' ? [] : {}) : undefined;
        accept(item);
        stack.push({ kind, phase: 'first', value: item });
      } else {
        accept(token.value);
        if (
          !build &&
          array &&
          stack.length === 1 &&
          recordStart !== undefined
        ) {
          records.push([recordStart, token.end]);
          recordStart = undefined;
        }
      }
    }
  }
}

function* visibleMessageWork(
  value: unknown
): Generator<void, VisibleChatMessage | null> {
  yield;
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  if (record.role !== 'user' && record.role !== 'assistant') return null;
  if (
    record.messageType === 'tool_approval_request' ||
    record.messageType === 'tool_approval_response' ||
    record.messageType === 'tool_result'
  )
    return null;
  const streamObjects = Array.isArray(record.streamObjects)
    ? (record.streamObjects as StreamObject[])
    : [];
  const parts: string[] = [];
  for (const object of streamObjects) {
    yield;
    parts.push(mergeStreamContent([object]));
  }
  const content = parts.join('') || readableText(record.content);
  return content ? { role: record.role, content } : null;
}

function* parseMessagesWork(
  value: unknown
): Generator<void, VisibleChatMessage[]> {
  if (typeof value !== 'string' || !value.length) return [];
  try {
    // Validate the full stored JSON without constructing discarded records.
    // Then materialize only the newest visible turns. Ignored entries still
    // yield through the same native Send driver rather than hiding a long scan.
    const { array, records } = yield* new StoredHistoryReader(value).read(
      false
    );
    if (!array) return [];
    const messages: VisibleChatMessage[] = [];
    for (
      let index = records.length - 1;
      index >= 0 && messages.length < MAX_TRANSCRIPT_MESSAGES;
      index--
    ) {
      yield;
      const [start, end] = records[index];
      const parsed = yield* new StoredHistoryReader(value, start, end).read(
        true
      );
      const visible = yield* visibleMessageWork(parsed.value);
      if (visible) messages.push(visible);
    }
    return messages.reverse();
  } catch {
    return [];
  }
}

function truncate(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}

function truncateHeadTail(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  const marker = '\n[… transport guard …]\n';
  const available = Math.max(0, maxLength - marker.length);
  const headLength = Math.floor(available * 0.2);
  const tailLength = available - headLength;
  return `${value.slice(0, headLength).trimEnd()}${marker}${value
    .slice(-tailLength)
    .trimStart()}`;
}

export type AIChatSelectionContext = {
  labelSuffix: string | null;
  transcript: string | null;
  conversationParentId: string | null;
};

/**
 * Produces read-only analysis context for explicitly selected AI chat blocks.
 * Tool payloads and approvals are deliberately excluded: selection grants
 * access to visible conversation text, not authority to replay actions.
 */
export function* getAIChatSelectionContextWork(
  props: AIChatSelectionProps | null | undefined
): Generator<void, AIChatSelectionContext> {
  const messages = yield* parseMessagesWork(props?.messages);
  const firstUserMessage = messages.find(message => message.role === 'user');
  const labelSuffix = firstUserMessage
    ? truncate(firstUserMessage.content.replace(/\s+/g, ' '), MAX_LABEL_LENGTH)
    : null;
  const transcript = messages.length
    ? truncateHeadTail(
        messages
          .map(
            message =>
              `${message.role === 'user' ? 'User' : 'Assistant'}: ${message.content}`
          )
          .join('\n'),
        MAX_TRANSCRIPT_LENGTH
      )
    : null;

  return {
    labelSuffix,
    transcript,
    conversationParentId:
      typeof props?.parentBlockId === 'string' && props.parentBlockId
        ? props.parentBlockId
        : null,
  };
}

/** Existing synchronous projection consumes the same stored-history reader. */
export function getAIChatSelectionContext(
  props: AIChatSelectionProps | null | undefined
): AIChatSelectionContext {
  const work = getAIChatSelectionContextWork(props);
  let step = work.next();
  while (!step.done) step = work.next();
  return step.value;
}
